import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Img,
  Text,
  Link as ChakraLink,
  Button,
  useColorMode,
} from "@chakra-ui/react";

const ArticlesPage = () => {
  const [showFullTopArticle, setShowFullTopArticle] = useState(false);
  const [showFullSecondArticle, setShowFullSecondArticle] = useState(false);
  const [showFullThirdArticle, setShowFullThirdArticle] = useState(false);
  const [showFullFourthArticle, setShowFullFourthArticle] = useState(false);
  const [showFullGccArticle, setShowFullGccArticle] = useState(false);
  
  // Get current color mode
  const { colorMode } = useColorMode();

  // Add meta tags for SEO
  useEffect(() => {
    // Set document title
    document.title = "Aptofy Articles - AI Recruitment Insights & Trends | Aptofy";
    
    // Create or update meta tags
    const metaTags = [
      { name: "description", content: "Discover the latest insights on AI-powered recruitment, hiring trends, and talent acquisition strategies. Read expert articles on modern recruitment technology and best practices." },
      { name: "keywords", content: "Aptofy articles, best AI hiring platform, hire faster, AI recruitment, automated candidate screening, bias-free hiring, video interviews, talent acquisition, recruitment technology, HR insights, artificial intelligence hiring, candidate experience, recruitment articles, smart hiring solutions, AI-powered interviews, recruitment automation, hiring efficiency, modern recruitment, AI hiring tools, recruitment AI, intelligent hiring, recruitment innovation, candidate assessment, hiring technology, AI recruiting software, QR verifiable offer letters, verify offer letter authenticity, best AI tools for candidate screening, DEI-compliant hiring platform, bias-free AI interview tools, employer branding remote hiring, replace walk-ins with remote hiring tech, sustainable recruitment solution enterprises, reduce hiring costs automation, improve candidate experience AI, AI recruiting software fast hiring, talent acquisition platform growth-stage companies, eco-friendly hiring solutions, automated candidate assessment tools, AI interview tools unbiased hiring, secure verifiable digital offer letters, virtual hiring events, no walk-in interviews, tech-enabled talent sourcing, remote recruitment solutions, enhanced candidate evaluation, employer branding strategies, reducing drop-offs better candidate experience, impact candidate experience employer branding, candidate-centric hiring, positive candidate experience" },
      { name: "author", content: "Aptofy Team" },
      { name: "robots", content: "index, follow" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      
      // Open Graph meta tags for social media
      { property: "og:title", content: "Aptofy Articles - Best AI Hiring Platform | Hire Faster with Smart Recruitment" },
      { property: "og:description", content: "Explore cutting-edge articles on AI-powered recruitment, hiring innovations, and talent acquisition strategies from Aptofy's expert team." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: window.location.href },
      { property: "og:site_name", content: "Aptofy" },
      { property: "og:image", content: "/aptofyinsight9.png" },
      
      // Twitter Card meta tags
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Aptofy Articles - AI Recruitment Insights & Trends" },
      { name: "twitter:description", content: "Discover the latest insights on AI-powered recruitment and modern hiring strategies." },
      { name: "twitter:image", content: "/aptofyinsight9.png" },
      
      // Additional meta tags
      { name: "theme-color", content: "#2563eb" },
      { name: "application-name", content: "Aptofy" },
      { name: "msapplication-TileColor", content: "#2563eb" },
    ];

    // Function to create or update meta tag
    const setMetaTag = (tagInfo) => {
      const selector = tagInfo.name ? `meta[name="${tagInfo.name}"]` : `meta[property="${tagInfo.property}"]`;
      let metaTag = document.querySelector(selector);
      
      if (!metaTag) {
        metaTag = document.createElement('meta');
        if (tagInfo.name) metaTag.setAttribute('name', tagInfo.name);
        if (tagInfo.property) metaTag.setAttribute('property', tagInfo.property);
        document.head.appendChild(metaTag);
      }
      
      metaTag.setAttribute('content', tagInfo.content);
    };

    // Apply all meta tags
    metaTags.forEach(setMetaTag);

    // Add canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', window.location.href);

    // Add structured data for articles
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Aptofy Articles",
      "description": "AI recruitment insights and modern hiring trends",
      "url": window.location.href,
      "publisher": {
        "@type": "Organization",
        "name": "Aptofy",
        "url": "https://www.aptofy.com"
      }
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]');
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(structuredData);

    // Cleanup function
    return () => {
      // Reset title when component unmounts
      document.title = "Aptofy";
    };
  }, []);

  const articles = [
    {
      imageUrl: "/aptofyinsight12.png",
      title: "The Hidden Cost of Connectivity: Unpacking Carbon Emissions from Our Digital Lives",
      content: (
        <>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
            In our increasingly digital world, the convenience of instant communication and access to vast information comes with a
            significant, often unseen, environmental footprint. From casual data transfers to intensive video calls and the rise of
            Large MultiModal Models (LMMs), our online activities contribute to carbon emissions. Even routine HD video calls can dwarf the
            footprint of a simple document transfer, and LMM queries are orders of magnitude heavier due to GPU/TPU compute and cooling needs.
            {!showFullTopArticle && ' '} 
            {!showFullTopArticle && (
              <>
                <Button
                  mt={2}
                  variant="link"
                  color="blue.500"
                  fontSize="lg"
                  onClick={() => setShowFullTopArticle(true)}
                  p={0}
                  h="auto"
                  fontWeight="normal"
                >
                  See More...
                </Button>
              </>
            )}
            {showFullTopArticle && (
              <span>
                {" "}
                Imagine sending a document with a few images—this generates only a few milligrams of CO₂. Contrast that with a 30-minute
                HD video call, which can produce tens to over a hundred grams of CO₂ due to continuous, high-volume streaming.
                The most profound impact, however, comes from LMMs whose GPU/TPU-heavy computation and cooling needs make a single
                interaction hundreds or thousands of times more energy-intensive than a basic request.
                {" "}
                Globally, hundreds of millions of online interviews add up to a staggering annual footprint. One 30-minute HD video call
                per day for a year could require the carbon absorption of roughly five mature trees. With video comprising the majority of
                internet data traffic, these emissions are set to grow.
                {" "}
                We must balance technological progress with responsibility—optimize algorithms and hardware efficiency, power data centers
                with renewables, and make conscious user choices like lower-resolution video or transferring only required data.
              </span>
            )}
          </Text>
          {showFullTopArticle && (
            <Text fontSize="lg" mt={3}>
              <ChakraLink
                href="https://www.linkedin.com/pulse/hidden-cost-connectivity-unpacking-carbon-emissions-from-gupta-adfxc?utm_source=share&utm_medium=member_android&utm_campaign=share_via"
                color="blue.500"
                textDecoration="underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Read the full article on LinkedIn
              </ChakraLink>
            </Text>
          )}
        </>
      ),
      linkedinLink:
        "https://www.linkedin.com/pulse/hidden-cost-connectivity-unpacking-carbon-emissions-from-gupta-adfxc?utm_source=share&utm_medium=member_android&utm_campaign=share_via",
    },
    {
      imageUrl: "/aptofyinsight9.png",
      title: "Offer Letter Authenticity Isn't Optional Anymore—it's Brand Critical",
      content: (
        <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
          In a world where scams are rising, <strong>building trust with candidates starts with the offer letter</strong>. 
          Cases like the <strong>ISRO job fraud in Bengaluru</strong> and <strong>over 50 job seekers duped in Hyderabad</strong> show 
          how critical it is to protect both candidates and company credibility. By adopting verifiable offer letters, 
          we don't just reduce fraud—we reinforce professionalism, improve candidate experience, and elevate our employer brand. 
          Offer letter scams can occur through various insidious methods. Scammers can mimic official company templates and use 
          fake domain emails, leading unsuspecting candidates to believe they are interacting with the legitimate organization. 
          <strong> Trust is no longer optional—it must be embedded from the offer stage itself.</strong>
        </Text>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/offer-letter-authenticity-isnt-optional-anymoreits-brand-gupta-ar2dc/"
    },
    {
      imageUrl: "/aptofyinsight11.png",
      title: "Why GCCs in India Must Automate Recruitment Now",
      content: (
        <>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
            “The war for talent is over. The robots, in the hands of humane leaders, have won.” GCCs in India are shifting from cost centers to
            innovation hubs—but traditional manual recruitment is now a serious bottleneck at scale. The ecosystem is massive and growing: ~1,700+
            GCCs today (50%+ of global), projected to cross 2,100 by 2030 with ~150 new centers annually, employing ~1.9M professionals and on track for
            2.5–2.8M by 2030.
            {showFullGccArticle && (
              <span>
                {" "}
                <strong>The Scale of the Challenge</strong>
                {" "}India hosts ~1,700 GCCs (50%+ of global), projected to cross 2,100 by 2030 with ~150 new centers annually.
                They employ ~1.9M professionals today, expected to grow to 2.5–2.8M by 2030. In FY 2024–25 alone, GCCs hired 100,000+ professionals—outpacing traditional IT services firms.
                {" "}
                Ask any GCC recruiter: screening thousands of resumes by hand is nearly impossible at GCC scale.
                {" "}
                <strong>The Hurdles Facing GCC Recruitment</strong>
                {" "}Massive volume, niche skill scarcity, quality vs. speed tradeoffs, long processes that hurt candidate experience, human bias, attrition risk from delays, and brand damage from inefficiency.
                {" "}
                <strong>The Automation Solution</strong>
                {" "}Smart screening to instantly shortlist by skills and fit. Intelligent, AI-led interviews to assess communication, technical skills, and cultural fit while reducing bias. Better candidate experience via instant feedback and smooth scheduling. Data-driven insights across the funnel. Scalable volume handling without multiplying headcount, letting recruiters focus on relationships and strategy.
                {" "}
                <strong>🌱 Sustainability Edge</strong>
                {" "}Remote, automated interviewing reduces unnecessary travel and cuts emissions, aligning GCC hiring with global sustainability goals while improving speed and efficiency.
                {" "}
                <strong>Smarter, Faster, Greener—The GCC Way Forward</strong>
                {" "}Automation transforms recruitment from a cost center into a strategic advantage that fuels innovation, equity, and sustainability. At this scale, transformation isn’t optional—it’s survival.
              </span>
            )}
          </Text>
          {!showFullGccArticle && (
            <Button
              mt={2}
              variant="link"
              color="blue.500"
              fontSize="lg"
              onClick={() => setShowFullGccArticle(true)}
              p={0}
              h="auto"
              fontWeight="normal"
            >
              See More...
            </Button>
          )}
        </>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/from-cost-center-innovation-hub-why-gccs-india-must-automate-gupta-lvx6f/"
    },
    {
      imageUrl: "/aptofyinsight10.png",
      title: "The Weekend Walk-in: A Glimpse into the Job Search Experience",
      content: (
        <>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
            For many professionals with up to 8 years of experience, <strong>the job search journey—especially during weekend walk-ins—can feel like a test of patience</strong>. 
            Meet Ananya—a fictional character, yet a reflection of thousands. She wakes up at <strong>6:30 AM</strong>, arrives at the venue by <strong>8:00 AM</strong> with her online registration in place.
            {showFullSecondArticle && (
              <span>
                {" "}Despite formal invites from HR agencies, what awaits her is confusion and delay. The panel meant to begin at <strong>9:00 AM</strong> shows up at <strong>10:00 AM</strong>. 
                By <strong>noon</strong>, the waiting room is still packed. Some roles are suddenly "closed," a few candidates are silently picked for second rounds, and most hear... nothing. 
                No feedback. No updates. No basic courtesy. <strong>It's not rejection that breaks morale—it's the absence of respect for effort and time.</strong> 
                This is the reality many job seekers face today. As organizations, we must remember: <strong>candidate experience is not a favour—it's a reflection of your brand.</strong> 
                The good news? Technology can fix this. <strong>Aptofy offers a full proof, AI-powered solution to streamline hiring, enhance engagement, and eliminate bias.</strong>
              </span>
            )}
          </Text>
          {!showFullSecondArticle && (
            <Button
              mt={2}
              variant="link"
              color="blue.500"
              fontSize="lg"
              onClick={() => setShowFullSecondArticle(true)}
              p={0}
              h="auto"
              fontWeight="normal"
            >
              See More...
            </Button>
          )}
        </>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/weekend-walk-glimpse-job-search-experience-manjari-gupta-123abc/"
    },
    {
      imageUrl: "/aptofyinsight7.png",
      title: "AI Recruitment Boom: Smarter, Faster, Fairer",
      content: (
        <>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
            The advent of <strong>Artificial Intelligence is fundamentally reshaping the recruitment landscape</strong>, 
            streamlining the entire hiring process from initial outreach to final selection. Recruiters are experiencing 
            significant advantages, benefiting immensely from <strong>automated candidate screening</strong> and the rapid, 
            precise shortlisting of top-fit individuals.
            {showFullThirdArticle && (
              <span>
                {" "}Concurrently, hiring managers are empowered with <strong>structured, largely bias-free evaluations</strong> and truly actionable insights, leading to more informed decisions. Candidates, too, are enjoying a more modernized and flexible experience, marked by the convenience of <strong>AI-led video interviews</strong> and notably quicker feedback loops, enhancing their overall journey. This profound shift is particularly evident in the Indian market, where a striking <strong>three out of four recruiters are now dedicating up to 70% of their hiring budgets towards AI solutions</strong>, underscoring a clear and decisive investment in this technology. This widespread adoption signals that AI in recruitment is not merely a trend, but rather <strong>the imperative of the hour</strong>, driving the evolution towards a hiring experience that is undeniably <strong>fairer, smarter, and considerably more efficient for all stakeholders involved.</strong>
              </span>
            )}
          </Text>
          {!showFullThirdArticle && (
            <Button
              mt={2}
              variant="link"
              color="blue.500"
              fontSize="lg"
              onClick={() => setShowFullThirdArticle(true)}
              p={0}
              h="auto"
              fontWeight="normal"
            >
              See More...
            </Button>
          )}
          {showFullThirdArticle && (
            <Text fontSize="lg" mt={3}>
              <ChakraLink
                href="https://hr.economictimes.indiatimes.com/news/trends/3-out-of-4-indian-recruiters-investing-up-to-70-hiring-budgets-in-ai/121810974"
                color="blue.500"
                textDecoration="underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Source: ET HRWorld
              </ChakraLink>
            </Text>
          )}
        </>
      ),
      linkedinLink: "https://hr.economictimes.indiatimes.com/news/trends/3-out-of-4-indian-recruiters-investing-up-to-70-hiring-budgets-in-ai/121810974",
      hideReadMore: true
    },
    {
      imageUrl: "/aptofyinsight8.png",
      title: "India's shift towards AI powered recruitment: Quality over speed or both",
      content: (
        <>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
            The global recruitment landscape is undergoing a profound shift, with <strong>AI tools</strong> increasingly 
            taking center stage. Recruiters worldwide are pivoting their focus from simply filling positions quickly to 
            prioritizing <strong>quality hiring</strong> and <strong>long-term organizational value</strong>. This evolution is transforming recruiters into <strong>strategic advisors</strong>, with a significant majority planning to expand their roles beyond traditional tasks.
            {showFullFourthArticle && (
              <span>
                {" "}AI is a powerful force driving this change, enabling <strong>smarter hiring decisions</strong> through <strong>reduced bias</strong>, <strong>improved candidate assessments</strong>, and the automation of administrative duties. This frees up recruiters' time for more <strong>human-centric engagement</strong>. Notably, <strong>India is at the forefront of this global AI adoption</strong>, leading the world in total AI deployment in business, with 59% of companies already implementing AI. As <strong>skills, culture fit, and retention</strong> become paramount, technology is no longer just an efficiency hack; it's a true enabler. This marks a fundamental transformation in the global recruitment mindset, where the precision of <strong>AI and the power of human empathy are increasingly working hand-in-hand</strong>.
              </span>
            )}
          </Text>
          {!showFullFourthArticle && (
            <Button
              mt={2}
              variant="link"
              color="blue.500"
              fontSize="lg"
              onClick={() => setShowFullFourthArticle(true)}
              p={0}
              h="auto"
              fontWeight="normal"
            >
              See More...
            </Button>
          )}
          {showFullFourthArticle && (
            <Text fontSize="lg" mt={3}>
              <ChakraLink
                href="https://economictimes.indiatimes.com/jobs/hr-policies-trends/indian-recruiters-pivot-to-quality-hiring-as-ai-tools-take-hold/articleshow/121794666.cms?utm_source=chatgpt.com"
                color="blue.500"
                textDecoration="underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Source: ET HRWorld
              </ChakraLink>
            </Text>
          )}
        </>
      ),
      linkedinLink: "https://economictimes.indiatimes.com/jobs/hr-policies-trends/indian-recruiters-pivot-to-quality-hiring-as-ai-tools-take-hold/articleshow/121794666.cms?utm_source=chatgpt.com",
      hideReadMore: true
    },
    {
      imageUrl: "/aptofyinsight.png",
      title: "Streamline hiring with AI",
      content: (
        <>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
            Streamline hiring, enhance candidate evaluations, and ensure a
            bias-free, efficient process. Embrace advanced AI and seamless video
            analysis to drive success.
          </Text>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
            Ready to transform your recruitment strategy?{" "}
            <ChakraLink
              href="https://www.aptofy.com/"
              color="blue"
              textDecoration="underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here
            </ChakraLink>{" "}
            to learn more and get started today!
          </Text>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>Book a Demo with us</Text>
          <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
            E-aptofycare@unlimiteye.com
          </Text>
        </>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/unlocking-organizational-success-power-managed-talent-acquisition-dipdc/?trackingId=ia6LgA%2FFrKvPYkIc77FN3g%3D%3D"
    },
    {
      imageUrl: "/aptofyinsight1.png",
      title: "Unlock candidate potential",
      content: (
        <Text fontSize="lg" ml={2} color={colorMode === 'dark' ? 'gray.200' : 'black'}>
          Unlock candidate potential with Aptofy's cutting-edge communication
          skill analysis. Elevate evaluation standards for hiring success. Our
          comprehensive approach ensures unbiased assessments, empowering your
          recruitment process. Partner with us to discover top-tier talent
          efficiently. Upgrade your hiring strategy with Aptofy today.
        </Text>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/elevating-candidate-evaluation-through-comprehensive-communication-fbvrc/?trackingId=5KJO8dNHzexeUAqzLG15lQ%3D%3D"
    },
    {
      imageUrl: "/aptofy insight2.png",
      title: "Adopting dark mode for energy efficiency",
      content: (
        <Text fontSize="lg" color={colorMode === 'dark' ? 'gray.200' : 'black'}>
          Using dark mode and darker screen colors cuts energy use and lowers electronic device carbon footprints. OLED and AMOLED screens, 
          especially in black, consume less power. Dark mode displays primarily black and dark gray, 
          reducing energy needs. Integrating deep blues and muted colors saves more energy, lessening charging
          cycles and overall electricity consumption. This conserves energy, cuts greenhouse gases, and encourages
          widespread adoption of Dark Mode
        </Text>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/world-environment-day-switch-dark-mode-aptofy-073wc/?trackingId=o%2FJ94A0waTmKPEjKA1LnNA%3D%3D"
    },
    {
      imageUrl: "/aptofy insight4.png",
      title: "AI bots revolutionizing recruitment",
      content: (
        <Text fontSize="lg" ml={2} color={colorMode === 'dark' ? 'gray.200' : 'black'}>
          The transformation of talent acquisition with artificial intelligence relieves employees from 
          the burdensome tasks of conducting interviews and sorting through resumes. This liberation allows
          them to redirect their time and energy towards their work, tasks, and personal lives, thereby
          fostering a healthier work-life balance and enhancing overall productivity.
        </Text>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/transformation-talent-acquisition-how-ai-bots-revolutionising-recruitment-m2poc/?trackingId=K2NkxeeCLj1yAoa%2BV0V2kQ%3D%3D",
    },
    {
      imageUrl: "/aptofy insight5.png",
      title: "Protecting trainers' intellectual property",
      content: (
        <Text fontSize="lg" ml={2} color={colorMode === 'dark' ? 'gray.200' : 'black'}>
          Protecting trainers' intellectual property (IP) is crucial in the digital age.
          Training materials and curated content are vulnerable to unauthorized use,
          undermining their value and discouraging innovation. Organizations can safeguard 
          these assets by restricting downloads, detecting misuse, ensuring exam integrity,
          and monitoring content. These measures preserve quality, foster trust, and uphold
          the credibility of learning programs.
        </Text>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/protecting-trainers-intellectual-property-learning-ecosystems-gupta-fz15c/?trackingId=tfGytdryTBun6C2ibSAAZg%3D%3D",
    },
    {
      imageUrl: "/aptofy insight6.png",
      title: "Empathy meets efficiency in AI recruitment",
      content: (
        <Text fontSize="lg" ml={2} color={colorMode === 'dark' ? 'gray.200' : 'black'}>
          In today's fast-paced world, talent acquisition is more than just hiring the right people;
          it's about fostering fairness, inclusivity, and efficiency. Artificial Intelligence (AI)
          is playing a pivotal role in transforming this process, making it both kinder and more 
          effective. Here's how AI is reshaping talent acquisition with empathy and efficiency.
        </Text>
      ),
      linkedinLink: "https://www.linkedin.com/pulse/empathy-meets-efficiency-how-ai-revolutionizing-talent-manjari-gupta-0lqoc/?trackingId=EVqtRFucQLeDHyIhuVJg3g%3D%3D",
    },
  ];

  return (
    <div>
      <h1
        style={{
          textAlign: "center",
          fontWeight: "bold",
          marginTop: "20px",
          fontSize: "26px",
          color: colorMode === 'dark' ? '#ffffff' : '#666',
        }}
      >
        Welcome to our articles page! 
      </h1>

      {articles.map((article, index) => (
        <Flex
          key={index}
          direction={{ base: "column", md: "row" }}
          align="center"
          justify="space-between"
          p={5}
          flexDirection={{ base: "column", md: index % 2 === 0 ? "row" : "row-reverse" }}
        >
          <Box 
            flex="1" 
            mr={{ base: 0, md: index % 2 === 0 ? 5 : 0 }} 
            mb={{ base: 4, md: 0 }} 
            mt={{ base: index === 0 ? 5 : 0 }}
            order={{ base: 1, md: 0 }}
          >
            {article.title && (
              <Text 
                fontSize="xl" 
                fontWeight="bold" 
                mb={3} 
                color={colorMode === 'dark' ? '#ffffff' : '#333'}
              >
                {article.title}
              </Text>
            )}
            {article.content}
            {!article.hideReadMore && (
              <Text fontSize="lg" mt={3}>
                <ChakraLink
                  href={article.linkedinLink}
                  color="blue.500"
                  textDecoration="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read More 
                </ChakraLink>
              </Text>
            )}
          </Box>

          <Box 
            flex="1" 
            mt={{ base: 3, md: 0 }} 
            ml={{ base: 0, md: index % 2 === 0 ? 0 : 5 }}
            order={{ base: 2, md: 0 }}
            w={{ base: "100%", md: "auto" }}
          >
            <img
              src={article.imageUrl}
              alt={article.title || "Insights Image"}
              style={{ 
                width: "100%", 
                height: "auto",
                objectFit: "contain",
                maxHeight: "400px"
              }}
            />
          </Box>
        </Flex>
      ))}
    </div>
  );
};

export default ArticlesPage;